import { useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { Profile, validateProfile, UpdateProfileParams, normalizeAvatarProfile } from '@/types/profile';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { oauthAnalytics } from '@/services/oauthAnalyticsService';
import { useProfileAvatarSync } from '@/hooks/useAvatarSync';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthStatusKnown, setIsAuthStatusKnown] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const authActionInProgress = useRef(false);
  const navigate = useNavigate();

  // Sincronizar avatar com useAvatarSync quando o perfil mudar
  const avatarUrl = useProfileAvatarSync(profile);

  // Forçar atualização do avatar quando o perfil for carregado (especialmente após refresh)
  useEffect(() => {
    if (profile && profile.avatar_url) {
      console.log("Avatar URL loaded from profile:", profile.avatar_url);
      // O useProfileAvatarSync já cuida da sincronização, mas vamos garantir
      // que o avatar seja atualizado mesmo após refresh
    }
  }, [profile?.avatar_url]);

  // Debug log para avatar sincronizado
  useEffect(() => {
    if (avatarUrl) {
      console.log("Synced avatar URL from useAvatarSync:", avatarUrl);
    }
  }, [avatarUrl]);

  // Helper function to fetch profile with error handling
  const fetchProfile = useCallback(async (userId: string) => {
    if (!userId) return null;

    try {
      console.log("Fetching profile for user:", userId);
      // Hotfix VPS compartilhado (wacrm): profiles tem PK id != user_id e colunas
      // monolab (username/website/company_id/is_admin) podem faltar. Usar select('*')
      // e tentar id primeiro (monolab), depois user_id (wacrm) para não quebrar 400.
      let data: any = null;
      let error: any = null;
      {
        const res = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
        data = res.data; error = res.error;
      }
      if (error) {
        console.error("Error fetching profile by id:", error);
        // se for 42703 column does not exist, já mitigado por select('*'), mas manter fallback
        const res2 = await supabase.from('profiles').select('*').eq('user_id', userId).maybeSingle();
        if (!res2.error && res2.data) { data = res2.data; error = null; }
        else if (res2.error) { console.error("Error fetching profile by user_id:", res2.error); return null; }
      }
      if (!data) {
        // Tentar fallback por user_id (wacrm) antes de criar
        const { data: byUserId, error: byUserErr } = await supabase
          .from('profiles').select('*').eq('user_id', userId).maybeSingle();
        if (!byUserErr && byUserId) {
          console.log("Profile found by user_id (wacrm):", byUserId);
          const validatedFallback = validateProfile(byUserId as any);
          // validateProfile espera shape monolab; para wacrm, mapear manualmente
          const mapped: any = validatedFallback ?? {
            id: (byUserId as any).user_id ?? (byUserId as any).id,
            full_name: (byUserId as any).full_name ?? null,
            avatar_url: (byUserId as any).avatar_url ?? null,
            username: (byUserId as any).username ?? null,
            website: (byUserId as any).website ?? null,
            company_id: (byUserId as any).company_id ?? null,
            is_admin: (byUserId as any).is_admin ?? false,
            updated_at: (byUserId as any).updated_at ?? null,
          };
          const normalizedFallback = normalizeAvatarProfile(mapped as any);
          setProfile(normalizedFallback);
          setIsAdmin((normalizedFallback as any)?.is_admin ?? false);
          return normalizedFallback;
        }
        if (byUserErr) console.error("Error fetching profile by user_id fallback:", byUserErr);
      }

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }

      // If profile doesn't exist, create it
      if (!data) {
        console.log("Profile not found. Creating new profile...");
        // Tentar insert monolab; se falhar por schema wacrm (account_id NOT NULL), tentar wacrm
        let newProfile: any = null;
        let createError: any = null;
        {
          const res = await supabase.from('profiles').insert([{ id: userId }]).select('*').single();
          newProfile = res.data; createError = res.error;
        }
        if (createError) {
          console.warn("Monolab insert failed, trying wacrm fallback:", createError.message);
          // Buscar account padrão para wacrm
          const { data: acct } = await supabase.from('accounts' as any).select('id').limit(1).maybeSingle() as any;
          const accountId = (acct as any)?.id;
          if (accountId) {
            const { data: emailData } = await supabase.auth.getUser();
            const email = emailData?.user?.email ?? `${userId}@example.com`;
            const wacrmPayload: any = { user_id: userId, full_name: email.split('@')[0], email, account_id: accountId, account_role: 'owner' };
            const res2 = await supabase.from('profiles').insert([wacrmPayload]).select('*').single();
            newProfile = res2.data; createError = res2.error;
          }
        }

        if (createError) {
          console.error("Error creating profile:", createError);
          return null;
        }

        console.log("New profile created:", newProfile);

        // Valida e normaliza o novo profile
        const validatedProfile = validateProfile(newProfile);
        if (!validatedProfile) {
          console.error("Invalid new profile data");
          return null;
        }

        const normalizedProfile = normalizeAvatarProfile(validatedProfile);
        setProfile(normalizedProfile);
        setIsAdmin(normalizedProfile.is_admin ?? false);
        return normalizedProfile;
      }

      console.log("Profile fetched:", data);

      // Valida e normaliza o profile para garantir consistência do avatar
      const validatedProfile = validateProfile(data);
      if (!validatedProfile) {
        console.error("Invalid profile data received from Supabase");
        return null;
      }

      // Normaliza para garantir que avatar_url seja sempre string | null (nunca undefined)
      const normalizedProfile = normalizeAvatarProfile(validatedProfile);

      setProfile(normalizedProfile);
      setIsAdmin(normalizedProfile.is_admin ?? false);
      return normalizedProfile;
    } catch (error) {
      console.error("Error in fetchProfile:", error);
      return null;
    }
  }, []);

  // Setup auth state listener
  useEffect(() => {
    console.log("Setting up auth state listener");
    
    // First set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        
        // Only do synchronous updates inside the callback to prevent deadlocks
        setSession(session);
        setUser(session?.user ?? null);
        setIsAuthStatusKnown(true);
        
        if (session?.user) {
          // Defer any Supabase calls with setTimeout to prevent deadlocks
          setTimeout(() => {
            // Check if this is an OAuth user by checking app_metadata
            const isOAuthUser = session.user.app_metadata?.provider === 'google' ||
                               session.user.app_metadata?.providers?.includes('google');

            if (isOAuthUser) {
              console.log("OAuth user detected, creating/updating profile");
              // Track OAuth success
              oauthAnalytics.trackOAuthSuccess('google', 'login_success', session.user.id);
              createOrUpdateProfileFromOAuth(session.user);
            } else {
              fetchProfile(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Then check for existing session (this order is important)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log("Initial session check:", session?.user?.id);

      if (session?.user) {
        const { data: verified, error: verifyError } = await supabase.auth.getUser();
        const invalidSession = verifyError
          ? [400, 401, 403].includes(verifyError.status ?? 0)
          : !verified?.user;
        if (invalidSession) {
          await supabase.auth.signOut({ scope: "local" });
          setSession(null);
          setUser(null);
          setProfile(null);
          setIsAuthStatusKnown(true);
          setLoading(false);
          return;
        }
      }

      setSession(session);
      setUser(session?.user ?? null);
      setIsAuthStatusKnown(true);

      if (session?.user) {
        // Check if this is an OAuth user by checking app_metadata
        const isOAuthUser = session.user.app_metadata?.provider === 'google' ||
                           session.user.app_metadata?.providers?.includes('google');

        if (isOAuthUser) {
          console.log("OAuth user detected in initial session, creating/updating profile");
          // Don't track success here as it's already tracked in auth state change
          createOrUpdateProfileFromOAuth(session.user).finally(() => {
            setLoading(false);
          });
        } else {
          fetchProfile(session.user.id).finally(() => {
            setLoading(false);
          });
        }
      } else {
        setLoading(false);
      }
    }).catch(error => {
      console.error("Error getting initial session:", error);
      setLoading(false);
      setIsAuthStatusKnown(true);
    });

    return () => {
      console.log("Cleaning up auth state listener");
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Login function with better error handling and state management
  const login = useCallback(async (email: string, password: string) => {
    if (authActionInProgress.current) {
      console.log("Auth action already in progress, ignoring request");
      return;
    }
    
    try {
      authActionInProgress.current = true;
      setAuthError(null);
      console.log("Attempting login for:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Login error:", error);
        setAuthError(error.message);
        throw error;
      }
      
      console.log("Login successful:", data?.user?.id);
      toast.success('Login realizado com sucesso!');
      return data;
    } catch (error: any) {
      console.error("Login exception:", error);
      toast.error(error.message || 'Erro ao fazer login');
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, []);

  // Register function with better error handling
  const register = useCallback(async (email: string, password: string, fullName: string) => {
    if (authActionInProgress.current) {
      console.log("Auth action already in progress, ignoring request");
      return;
    }
    
    try {
      authActionInProgress.current = true;
      setAuthError(null);
      console.log("Registering new user:", email);
      
      if (!email || !password) {
        throw new Error("E-mail e senha são obrigatórios");
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) {
        console.error("Registration error from Supabase:", error);
        setAuthError(error.message);
        
        // Check for specific error messages that indicate user already exists
        if (error.message?.includes('already registered') || 
            error.message?.includes('user_already_exists') ||
            error.message?.includes('duplicate') ||
            error.message?.includes('A user with this email address has already been registered')) {
          console.log("Supabase error indicates user already exists:", error.message);
          const userExistsError = new Error('user_already_exists');
          userExistsError.message = 'Este e-mail já está cadastrado. Por favor, faça login.';
          setAuthError('user_already_exists');
          throw userExistsError;
        }
        
        throw error;
      }
      
      // Check if user already exists (Supabase may return user object with empty identities)
      if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        console.log("User already exists - showing dialog for:", email);
        const userExistsError = new Error('user_already_exists');
        userExistsError.message = 'Este e-mail já está cadastrado. Por favor, faça login.';
        setAuthError('user_already_exists');
        throw userExistsError;
      }
      
      // Check if email confirmation is required
      const isEmailConfirmationRequired = !data?.session;
      
      if (isEmailConfirmationRequired) {
        console.log("Email confirmation required for:", email);
        toast.success('Conta criada com sucesso! Por favor, verifique seu email para confirmar o cadastro.');
      } else {
        console.log("Registration successful, auto-signed in:", data?.user?.id);
        toast.success('Conta criada com sucesso!');
      }
      
      return { success: true, emailConfirmationRequired: isEmailConfirmationRequired };
    } catch (error: any) {
      console.error("Registration exception:", error);
      
      // Don't show toast for user_exists error, as it will be handled by the dialog
      if (error.message !== 'user_already_exists' && !error.message.includes('já está cadastrado')) {
        toast.error(error.message || 'Erro ao criar conta');
      }
      
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, []);

  // Logout function with better error handling
  const logout = useCallback(async () => {
    if (authActionInProgress.current) return;
    
    try {
      authActionInProgress.current = true;
      console.log("Logging out user");
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Logout error:", error);
        throw error;
      }
      
      console.log("Logout successful");
      navigate('/login');
      toast.success('Logout realizado com sucesso!');
    } catch (error: any) {
      console.error("Logout exception:", error);
      toast.error(error.message || 'Erro ao fazer logout');
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, [navigate]);

  const updateProfile = useCallback(async (data: UpdateProfileParams) => {
    if (!user) {
      console.error("Cannot update profile: User not logged in");
      throw new Error('Usuário não está logado');
    }

    try {
      console.log("Updating profile for user:", user.id, data);

      // Ensure profile exists before update
      if (!profile) {
        await fetchProfile(user.id);
      }

      // CRÍTICO: Só incluir avatar_url no update se ele estiver explicitamente em data
      // Se não estiver, deixar o banco manter o valor atual (não sobrescrever com estado local desatualizado)
      const updateData: any = { ...data };
      
      // Se avatar_url está sendo atualizado explicitamente, limpar cache-busting
      if (data.avatar_url !== undefined) {
        let cleanAvatarUrl = data.avatar_url;
        if (cleanAvatarUrl && typeof cleanAvatarUrl === 'string') {
          // Remove qualquer parâmetro de query (cache-busting) da URL
          cleanAvatarUrl = cleanAvatarUrl.split('?')[0];
        }
        updateData.avatar_url = cleanAvatarUrl;
      }
      // Se avatar_url não está em data, NÃO incluir no updateData (deixa banco manter valor atual)

      console.log("Final update data (with clean avatar_url):", updateData);

      const { data: updatedProfile, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select('*') // Select explicit columns including avatar_url
        .single();

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }

      console.log("Profile updated successfully:", updatedProfile);

      // Validate and convert to safe profile type
      const safeProfile = validateProfile(updatedProfile);
      if (!safeProfile) {
        throw new Error('Erro ao validar perfil atualizado');
      }

      // Update the local state with the validated profile data
      setProfile(safeProfile);

      return safeProfile; // Return the validated profile data

    } catch (error: any) {
      console.error("Error in updateProfile:", error);
      throw error;
    }
  }, [user, profile, fetchProfile]);

  // Helper function to create/update profile from OAuth user data
  const createOrUpdateProfileFromOAuth = useCallback(async (user: User) => {
    if (!user?.id) return null;

    try {
      console.log("Creating/updating OAuth profile for user:", user.id);

      // Extract user data from OAuth provider
      const userData = {
        full_name: user.user_metadata?.full_name || 
                  user.user_metadata?.name || 
                  user.email?.split('@')[0] || 'Usuário',
        avatar_url: user.user_metadata?.avatar_url || 
                   user.user_metadata?.picture || null,
        email: user.email
      };

      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (existingProfile) {
        // Update existing profile
        const { data: updatedProfile, error } = await supabase
          .from('profiles')
          .update({
            full_name: userData.full_name,
            avatar_url: userData.avatar_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id)
          .select('*')
          .single();

        if (error) {
          console.error("Error updating OAuth profile:", error);
          return null;
        }

        console.log("OAuth profile updated:", updatedProfile);

        // Valida e normaliza o profile OAuth atualizado
        const validatedProfile = validateProfile(updatedProfile);
        if (!validatedProfile) {
          console.error("Invalid OAuth profile data");
          return null;
        }

        const normalizedProfile = normalizeAvatarProfile(validatedProfile);
        setProfile(normalizedProfile);
        setIsAdmin(normalizedProfile.is_admin ?? false);
        return normalizedProfile;
      } else {
        // Create new profile
        const { data: newProfile, error } = await supabase
          .from('profiles')
          .insert([{
            id: user.id,
            full_name: userData.full_name,
            avatar_url: userData.avatar_url
          }])
          .select('*')
          .single();

        if (error) {
          console.error("Error creating OAuth profile:", error);
          return null;
        }

        console.log("OAuth profile created:", newProfile);

        // Valida e normaliza o novo profile OAuth
        const validatedProfile = validateProfile(newProfile);
        if (!validatedProfile) {
          console.error("Invalid new OAuth profile data");
          return null;
        }

        const normalizedProfile = normalizeAvatarProfile(validatedProfile);
        setProfile(normalizedProfile);
        setIsAdmin(normalizedProfile.is_admin ?? false);
        return normalizedProfile;
      }
    } catch (error) {
      console.error("Error in createOrUpdateProfileFromOAuth:", error);
      return null;
    }
  }, []);

  // Login with Google OAuth
  const loginWithGoogle = useCallback(async () => {
    if (authActionInProgress.current) {
      console.log("Auth action already in progress, ignoring request");
      return;
    }

    try {
      authActionInProgress.current = true;
      setAuthError(null);
      console.log("Attempting Google OAuth login");

      // Track OAuth login attempt
      oauthAnalytics.startOAuthTracking('google', 'login_attempt');

      // Get the production redirect URL if configured
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || `${window.location.origin}/dashboard`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error("Google OAuth error:", error);
        setAuthError(error.message);
        oauthAnalytics.trackOAuthError('google', 'login_error', error.message);
        throw error;
      }

      console.log("Google OAuth initiated successfully");
      // Success will be tracked when the auth state changes
      return data;
    } catch (error: any) {
      console.error("Google OAuth exception:", error);
      oauthAnalytics.trackOAuthError('google', 'login_error', error.message);
      toast.error(error.message || 'Erro ao fazer login com Google');
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, []);

  // Register with Google OAuth (same as login, creates user on first use)
  const registerWithGoogle = useCallback(async () => {
    if (authActionInProgress.current) {
      console.log("Auth action already in progress, ignoring request");
      return;
    }

    try {
      authActionInProgress.current = true;
      setAuthError(null);
      console.log("Attempting Google OAuth registration");

      // Track OAuth registration attempt
      oauthAnalytics.startOAuthTracking('google', 'register_attempt');

      // Get the production redirect URL if configured
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || `${window.location.origin}/dashboard`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error("Google OAuth registration error:", error);
        setAuthError(error.message);
        oauthAnalytics.trackOAuthError('google', 'register_error', error.message);
        throw error;
      }

      console.log("Google OAuth registration initiated successfully");
      // Success will be tracked when the auth state changes
      return data;
    } catch (error: any) {
      console.error("Google OAuth registration exception:", error);
      oauthAnalytics.trackOAuthError('google', 'register_error', error.message);
      toast.error(error.message || 'Erro ao cadastrar com Google');
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, []);

  // Link Google account to existing user
  const linkGoogleAccount = useCallback(async () => {
    if (!user) {
      console.error("Cannot link Google account: User not logged in");
      throw new Error('Usuário não está logado');
    }

    if (authActionInProgress.current) {
      console.log("Auth action already in progress, ignoring request");
      return;
    }

    try {
      authActionInProgress.current = true;
      setAuthError(null);
      console.log("Linking Google account to user:", user.id);

      // Get the production redirect URL if configured
      const redirectUrl = import.meta.env.VITE_OAUTH_REDIRECT_URL || `${window.location.origin}/dashboard`;

      const { data, error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error("Link Google account error:", error);
        setAuthError(error.message);
        throw error;
      }

      console.log("Google account linking initiated successfully");
      toast.success('Conta Google vinculada com sucesso!');
      return data;
    } catch (error: any) {
      console.error("Link Google account exception:", error);
      toast.error(error.message || 'Erro ao vincular conta Google');
      throw error;
    } finally {
      authActionInProgress.current = false;
    }
  }, [user]);

  // Reset auth errors and state
  const resetAuthState = useCallback(() => {
    setAuthError(null);
    authActionInProgress.current = false;
  }, []);

  return {
    user,
    session,
    profile,
    loading,
    isAuthStatusKnown,
    authError,
    isAdmin,
    login,
    register,
    logout,
    updateProfile,
    fetchProfile,
    createOrUpdateProfileFromOAuth,
    loginWithGoogle,
    registerWithGoogle,
    linkGoogleAccount,
    resetAuthState,
    isAuthActionInProgress: () => authActionInProgress.current,
    avatarUrl // Avatar sincronizado com cache-busting automático
  };
}
